import React from 'react';
import { flexRender } from '@tanstack/react-table';
import {
  Users,
  UserPlus,
  Filter,
  MoreHorizontal,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { EmptyState } from '../../components/ui/PageLayout';
import SearchBar from '../../components/ui/SearchBar';

export default function MembersListTab({
  members,
  hasListFilters,
  search,
  setSearch,
  memberFilter,
  setMemberFilter,
  setDraftFilter,
  setFilterModalOpen,
  hasActiveScopeFilter,
  canManageMembers,
  moreMenuRef,
  moreMenuOpen,
  setMoreMenuOpen,
  setImportOpen,
  setGithubSuggestOpen,
  setAddMemberOpen,
  table,
  pageInput,
  setPageInput,
  goToMember,
  handleRowActivate,
}: any) {
  return (
    <div role="tabpanel" id="members-tabpanel-active" aria-labelledby="members-tab-active">
      <section>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="w-full min-w-[12rem] max-w-md sm:w-64 sm:max-w-md flex-shrink-0">
            <SearchBar value={search} onChange={setSearch} placeholder="Search members…" />
          </div>
          <button
            type="button"
            onClick={() => {
              setDraftFilter({ ...memberFilter });
              setFilterModalOpen(true);
            }}
            className={`btn-icon-mdl h-10 w-10 rounded-xl flex-shrink-0 border ${
              hasActiveScopeFilter
                ? 'border-vivid-red text-vivid-red bg-vivid-red/10'
                : 'border-dark-border text-slate-400'
            }`}
            aria-label="Filter members by stream, project, or repository"
            title="Filter by stream, project, or repository"
          >
            <Filter className="w-5 h-5" />
          </button>
          {canManageMembers ? (
            <div className="relative ml-auto" ref={moreMenuRef}>
              <button
                type="button"
                onClick={() => setMoreMenuOpen((o) => !o)}
                className="btn-icon-mdl h-10 w-10 rounded-xl border border-dark-border text-slate-400"
                aria-label="More actions"
                aria-expanded={moreMenuOpen}
                aria-haspopup="true"
              >
                <MoreHorizontal className="w-5 h-5" />
              </button>
              {moreMenuOpen && (
                <div className="absolute right-0 top-full z-20 mt-1 min-w-[180px] rounded-xl border border-dark-border bg-dark-surface py-1 shadow-xl shadow-black/50">
                  <button
                    type="button"
                    onClick={() => {
                      setImportOpen(true);
                      setMoreMenuOpen(false);
                    }}
                    className="nav-dropdown-item-mdl w-full text-left"
                  >
                    Import members
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setGithubSuggestOpen(true);
                      setMoreMenuOpen(false);
                    }}
                    className="nav-dropdown-item-mdl w-full text-left"
                  >
                    Link GitHub accounts
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </div>
        {members.length === 0 ? (
          <EmptyState
            icon={<Users className="w-12 h-12 text-slate-500" />}
            title={hasListFilters ? 'No members match your filters.' : 'No members yet.'}
            description={
              hasListFilters
                ? 'Try adjusting search or scope filters, or clear filters and search again.'
                : 'Invite someone to join your organisation.'
            }
            action={
              hasListFilters ? (
                <button
                  type="button"
                  onClick={() => {
                    setSearch('');
                    setMemberFilter({ streamId: '', projectId: '', repositoryId: '' });
                  }}
                  className="empty-state-cta-mdl inline-flex items-center gap-2"
                >
                  Clear filters
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setAddMemberOpen(true)}
                  className="empty-state-cta-mdl inline-flex items-center gap-2"
                  aria-label="Invite member"
                >
                  <UserPlus className="w-5 h-5" />
                  Invite
                </button>
              )
            }
          />
        ) : (
          <div className={`${table.wrapper} overflow-hidden`}>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-dark-border">
                <thead className={table.thead}>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          scope="col"
                          className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${table.th}`}
                        >
                          <div
                            className={`flex items-center justify-between group ${header.column.getCanSort() ? 'cursor-pointer select-none' : ''}`}
                            onClick={header.column.getToggleSortingHandler()}
                            onKeyDown={
                              header.column.getCanSort()
                                ? (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      header.column.getToggleSortingHandler()?.(e);
                                    }
                                  }
                                : undefined
                            }
                            role={header.column.getCanSort() ? 'button' : undefined}
                            tabIndex={header.column.getCanSort() ? 0 : undefined}
                            aria-sort={
                              header.column.getIsSorted() === 'asc'
                                ? 'ascending'
                                : header.column.getIsSorted() === 'desc'
                                  ? 'descending'
                                  : header.column.getCanSort()
                                    ? 'none'
                                    : undefined
                            }
                          >
                            <span>
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </span>
                            {header.column.getCanSort() && (
                              <span
                                className="ml-2 opacity-60 group-hover:opacity-100 transition-opacity"
                                aria-hidden
                              >
                                {header.column.getIsSorted() === 'asc' && (
                                  <ArrowUp className="w-4 h-4 text-vivid-red" />
                                )}
                                {header.column.getIsSorted() === 'desc' && (
                                  <ArrowDown className="w-4 h-4 text-vivid-red" />
                                )}
                                {!header.column.getIsSorted() && (
                                  <ArrowUpDown className="w-4 h-4 text-slate-500" />
                                )}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-dark-border bg-dark-surface">
                  {table.getRowModel().rows.map((row) => {
                    const m = row.original;
                    const rowLabel = m.username || m.email || 'Member';
                    return (
                      <tr
                        key={row.id}
                        tabIndex={0}
                        aria-label={`Open member ${rowLabel}`}
                        className={`${table.rowHover} cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-vivid-red/40`}
                        onClick={() => goToMember(m.id)}
                        onKeyDown={(e) => handleRowActivate(m.id, e)}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className={`px-6 py-4 max-w-[16rem] sm:max-w-none sm:whitespace-nowrap ${table.td}`}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {table.getPageCount() > 1 && (
              <div className="border-t border-dark-border bg-dark-surface px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:px-6">
                <p className="text-sm text-slate-300">
                  Showing{' '}
                  <span className="font-medium">
                    {table.getState().pagination.pageIndex * table.getState().pagination.pageSize +
                      1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(
                      (table.getState().pagination.pageIndex + 1) *
                        table.getState().pagination.pageSize,
                      table.getPrePaginationRowModel().rows.length
                    )}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium">
                    {table.getPrePaginationRowModel().rows.length}
                  </span>
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-300">Page</span>
                    <input
                      type="number"
                      min="1"
                      max={table.getPageCount()}
                      value={pageInput}
                      onChange={(e) => setPageInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const page = (e.target as HTMLInputElement).value
                            ? Number((e.target as HTMLInputElement).value) - 1
                            : 0;
                          if (page >= 0 && page < table.getPageCount()) {
                            table.setPageIndex(page);
                            setPageInput('');
                          }
                        }
                      }}
                      placeholder={`${table.getState().pagination.pageIndex + 1}`}
                      className="w-16 rounded-md border-dark-border shadow-sm focus:border-brand-purple focus:ring-brand-purple/30 text-sm px-2 py-1 border bg-dark-elevated text-slate-100 text-center"
                      aria-label="Go to page"
                    />
                    <span className="text-sm text-slate-500">of {table.getPageCount()}</span>
                  </div>
                  <nav
                    className="inline-flex rounded-md shadow-sm -space-x-px"
                    aria-label="Pagination"
                  >
                    <button
                      type="button"
                      onClick={() => table.previousPage()}
                      disabled={!table.getCanPreviousPage()}
                      className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-dark-border bg-dark-elevated text-sm font-medium text-slate-400 hover:bg-dark-base disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      onClick={() => table.nextPage()}
                      disabled={!table.getCanNextPage()}
                      className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-dark-border bg-dark-elevated text-sm font-medium text-slate-400 hover:bg-dark-base disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
